import { is, List } from "immutable";
import _ from "lodash";
import moment from "moment";
import * as QueryString from "querystringify";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter, Link } from "react-router-dom";
import * as redux from "redux";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { CareerCenterJobCategories } from "../../../domain";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { Utility } from "../../../utils";
import { CareerCenterHelper } from "../../../models/careerCenter/CareerCenterHelper";
import { SearchBox } from "../components";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    api: new Api(state),
    countries: state.countries.countries,
    jobs: state.careerCenter.jobs,
    loadingData: state.careerCenter.loadingJobs,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    loadData: () => dispatch(Actions.CareerCenter.loadJobs()),
    toggleLoading: (loading: boolean) => dispatch(Actions.CareerCenter.toggleJobsLoading(loading)),
    updateQuery: (query: UrlQuery, overwrite?: boolean) => dispatch(Actions.Router.updateQueryParams(query, overwrite)),
});

interface LocationFilter {
    country?: string;
    countryCode?: string;
    state?: string;
    stateCode?: string;
}

interface UrlQuery {
    browse?: string;
    cat?: string;
    loc?: string;
    q?: string;
    page?: string;
}

type OwnProps = RouteComponentProps<{
}> & {
};

interface ConnectedState {
    api: Api;
    countries: List<M.Country.Country>;
    jobs: List<M.CareerCenter.JobPost>;
    loadingData: boolean;
}

interface ConnectedDispatch {
    loadData: () => void;
    toggleLoading: (loading: boolean) => void;
    updateQuery: (query: UrlQuery, overwrite?: boolean) => void;
}

interface BrowseType {
    name: string;
    param: string;
    toggled: boolean;
}

interface OwnState {
    appliedFilters: M.Search.AppliedFilter[];
    doFilter: boolean;
    errors: string[];
    filteredData: List<M.CareerCenter.JobPost>;
    filters: {
        category: BrowseType;
        location: BrowseType;
    };
    scrollTop?: number;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class JobDirectoryComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _searchTimer: NodeJS.Timer | undefined;
    private _itemsPerPage = 10;

    public constructor(props: any) {
        super(props);
        this.state = {
            appliedFilters: [],
            doFilter: false,
            errors: [],
            filteredData: List(),
            filters: {
                category: { name: "category", param: "cat", toggled: false },
                location: { name: "location", param: "loc", toggled: false },
            },
            loading: false,
        };
    }

    public async componentDidMount() {
        this._isMounted = true;

        if (this.props.jobs.size) {
            this.filterData(this.props.jobs);
            this.onQueryUpdated(this.props.location.search);
        }
        else
            this.props.toggleLoading(true);

        await this.props.loadData();

        if (!this._isMounted)
            return;

        this.onQueryUpdated(this.props.location.search);
    }

    public componentDidUpdate(prevProps: CombinedProps, prevState: OwnState) {
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public componentWillReceiveProps(nextProps: Readonly<CombinedProps>, nextContext: any) {
        if (!is(this.props.jobs, nextProps.jobs))
            this.triggerDataFilter();
    }

    public shouldComponentUpdate?(nextProps: Readonly<CombinedProps>, nextState: Readonly<OwnState>, nextContext: any): boolean {

        if (nextState.doFilter) {
            this.filterData(nextProps.jobs);
            return false;
        }

        if (nextProps.location.search !== this.props.location.search) {
            this.onQueryUpdated(nextProps.location.search);
            return false;
        }

        return true;
    }

    public render() {
        const currentUrl = this.props.match.url + this.props.location.search;
        const query = this.getUrlQuery();
        const errors = this.state.errors.length
            ? <BS.Alert bsStyle="warning">
                <h3>Failed to display job directory</h3>
                <ul>
                    {this.state.errors.map((e, i) => (<li key={i}>{e.toString()}</li>))}
                </ul>
            </BS.Alert>
            : null;

        // create US/non-US location filters
        const locations = this.getUniqueLocations();
        const USLocations = locations.filter((x) => x.countryCode === "US" && x.state);
        const otherLocations = locations.filter((x) => x.countryCode !== "US");

        const usLocEl = USLocations.length
            ? <div className="filters">
                <ul className="cols4">
                    {USLocations.map((x, i) => (
                        <li key={i}>
                            <a href="#" onClick={(e) => this.onFilterClick(e, this.state.filters.location, `${x.stateCode}_${x.countryCode}`)}>{x.state}</a>
                        </li>
                    ))}
                </ul>
            </div>
            : null;
        const otherLocEl = otherLocations.length
            ? <div className="filters">
                <ul className="cols4">
                    {otherLocations.map((x, i) => (
                        <li key={i}>
                            <a href="#" onClick={(e) => this.onFilterClick(e, this.state.filters.location, `${x.stateCode}_${x.countryCode}`)}>{x.state}</a>
                        </li>
                    ))}
                </ul>
            </div>
            : null;
        const locHr = USLocations.length && otherLocations.length ? <hr /> : null;

        // create applied filters
        const appliedFilters = <Comp.Search.AppliedFilters
            filters={this.state.appliedFilters}
            onClearAll={() => this.onClearAllFilters()}
            onClearFilter={(id, type) => this.onClearFilter(id, type)}
        />;

        // build job lists
        const page = parseInt(query.page || "1", 10);
        const jobList = this.state.filteredData;
        const numOfLists = Math.ceil(jobList.size / this._itemsPerPage);
        const jobListElements: JSX.Element[] = [];

        for (let i = 1; i <= numOfLists; i++) {
            const minIdx = (i - 1) * this._itemsPerPage;
            const maxIdx = Math.min((i * this._itemsPerPage) - 1, jobList.size - 1);
            let subList: JSX.Element | JSX.Element[] = [];

            for (let k = minIdx; k <= maxIdx; k++) {
                const job = jobList.get(k);
                subList.push(<li key={k} id={"job-" + job.id}>
                    <Preview currentUrl={currentUrl} job={job} />
                </li>);
            }

            subList = <BS.Collapse key={i} in={i <= page} timeout={625}>
                <ul className={"jobs" + (i > 1 ? " more" : "")}>{subList}</ul>
            </BS.Collapse>;
            jobListElements.push(subList);
        }

        const showMore = page < numOfLists
            ? <BS.Button bsStyle="primary" className="center-block" onClick={() => this.onMoreClick()}>Show more&hellip;</BS.Button>
            : null;

        return <Comp.Page.PublicPage additionalClasses="full" pageTitle="Search Jobs" style={{ minHeight: "200px", position: "relative" }}>
            <Comp.Reusable.CountriesLoader />
            <Comp.Generic.LoadingSpinner show={this.state.loading || this.props.loadingData} />
            <Comp.Generic.WindowMotionScroll scrollTop={this.state.scrollTop} />

            <div id="content-primary">
                {errors}

                <SearchBox query={query.q} onSearch={(q) => this.onSearchChange(q)} />

                <h2>Browse</h2>

                <div className="browse">
                    <ul>
                        <li className={this.state.filters.category.toggled ? "active" : ""}>
                            <a href="#" onClick={(e) => this.onBrowseTypeClick(e, this.state.filters.category)}>Job category</a>
                        </li>
                        <li className={this.state.filters.location.toggled ? "active" : ""}>
                            <a href="#" onClick={(e) => this.onBrowseTypeClick(e, this.state.filters.location)}>Location</a>
                        </li>
                    </ul>
                </div>

                <BS.Collapse in={this.state.filters.category.toggled} timeout={125}>
                    <div className="filters">
                        <ul>
                            {CareerCenterJobCategories.Values.map((x, i) => (
                                <li key={i}>
                                    <a href="#" onClick={(e) => this.onFilterClick(e, this.state.filters.category, x.id)}>{x.name}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </BS.Collapse>

                <BS.Collapse in={this.state.filters.location.toggled} timeout={125}>
                    <div>
                        {usLocEl}
                        {locHr}
                        {otherLocEl}
                    </div>
                </BS.Collapse>

                {appliedFilters}

                <hr />

                <h2>Most recent</h2>

                {jobListElements}

                {showMore}
            </div>
            <div id="content-secondary"></div>
            <div className="clear"></div>
        </Comp.Page.PublicPage>;
    }

    private filterData(jobs: List<M.CareerCenter.JobPost>) {
        let filteredData: List<M.CareerCenter.JobPost> = List();
        const urlQuery = this.getUrlQuery();

        const page = parseInt(urlQuery.page || "1", 10);
        const search = decodeURIComponent(urlQuery.q || "").toLowerCase();
        const browseIds = (urlQuery.browse || ",").split(",").filter((x) => x !== "");
        const catIds = (urlQuery.cat || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        const locations = decodeURIComponent(urlQuery.loc || "|").split("|").filter((x) => x !== "")
            .map((x) => {
                // location string can be of format [state]_[country] or [country]
                const parts = x.split("_").filter((y) => y !== "");
                return {
                    stateCode: parts.length === 2 ? parts[0] : undefined,
                    countryCode: parts.length === 2 ? parts[1] : parts[0],
                };
            });

        _.orderBy(jobs.toArray(), ["postedOn"], ["desc"]).map((x, i) => {
            if (x) {
                // filter by category
                const catOK = !catIds.length || catIds.indexOf(x.categoryId) > -1;

                // filter by locations
                let locOK = locations.length === 0;
                if (catOK) {
                    for (const loc of locations) {
                        if (loc.countryCode !== "US" && x.location.countryCode === loc.countryCode)
                            locOK = true; // non-US
                        else if (loc.countryCode === "US" && x.location.countryCode === loc.countryCode && x.location.state === loc.stateCode)
                            locOK = true; // US, by state
                    }
                }

                // set description without html
                x.descriptionNoHtml = (Utility.decodeHtml(x.description) || "").replace(/\r\n/, " ");

                // filter by search
                let searchOK = !search || search.length < 2;
                if (catOK && locOK && search && search.length >= 2) {
                    const t = (x.title || "").toLowerCase();
                    const o = (x.organization || "").toLowerCase();
                    const d = (x.descriptionNoHtml || "").toLowerCase();
                    searchOK = t === search || t.indexOf(search + " ") > -1 || t.indexOf(" " + search) > -1 || t.startsWith(search) || t.endsWith(search)
                        || o === search || o.indexOf(search + " ") > -1 || o.indexOf(" " + search) > -1 || o.startsWith(search) || o.endsWith(search)
                        || d === search || d.indexOf(search + " ") > -1 || d.indexOf(" " + search) > -1 || d.startsWith(search) || d.endsWith(search);
                }

                if (catOK && locOK && searchOK) {
                    filteredData = filteredData.push(x);
                }
            }
        });

        const filters = this.state.filters;
        filters.category.toggled = false;
        filters.location.toggled = false;
        for (const b of browseIds) {
            const f = filters[b];
            if (f)
                f.toggled = true;
        }

        // make sure page is within range
        const pages = Math.ceil(filteredData.size / this._itemsPerPage);
        if (pages > 0 && page < 1)
            this.props.updateQuery({ page: "" });
        else if (pages > 0 && page > pages)
            this.props.updateQuery({ page: pages.toString() });
        else {
            this.setState({
                ...this.state,
                doFilter: false,
                filteredData,
                filters,
            });
        }
    }

    /**
     * Gets an array of unique job locations (by country/state)
     */
    private getUniqueLocations(): LocationFilter[] {
        const locations = this.props.jobs.map((j) => {
            if (!j || !j.location || !j.location.countryCode)
                return null;

            const country = this.props.countries.find((c) => c !== undefined && c.isoCode === j.location.countryCode);
            const state = country && country.states && country.states.length
                ? country.states.find((s) => s.code.toLowerCase() === (j.location.state || "").toLowerCase()
                    || s.name.toLowerCase() === (j.location.state || "").toLowerCase())
                : undefined;

            return {
                country: j.location.country,
                countryCode: j.location.countryCode,
                stateCode: j.location.state,
                state: state ? state.name : j.location.state,
            };
        })
        .toArray()
        .filter((l) => l !== null && l.country);

        return _.orderBy(
            _.uniqWith(locations, _.isEqual),
            ["country", "state"],
        );
    }

    private getUrlQuery(): UrlQuery {
        return QueryString.parse(this.props.location.search) as UrlQuery;
    }

    /**
     * Shows/hides browse filter
     * @param e Mouse click event
     * @param type Browse filter
     */
    private onBrowseTypeClick(e: React.MouseEvent<HTMLAnchorElement>, type: BrowseType) {
        const urlQuery = this.getUrlQuery();
        let browseIds = (urlQuery.browse || "").split(",").filter((x) => x !== "");
        const idx = browseIds.indexOf(type.name);
        if (idx > -1) {
            // currently open, so close
            browseIds = [];
        }
        else {
            // currently closed, so open
            browseIds = [type.name];
        }

        this.props.updateQuery({ browse: browseIds.join(",") });
        e.preventDefault();
        return false;
    }

    private onClearAllFilters() {
        this.props.updateQuery({ browse: "", q: "", cat: "", loc: "" });
    }

    private onClearFilter(id: string | number, type: string) {
        const urlQuery = this.getUrlQuery();
        const items = Utility.commaStringToArray(urlQuery[type]);
        Utility.addToOrRemoveFromArray(items, id, false);
        this.props.updateQuery({ [type]: items.join(",") });
    }

    /**
     * Adds the given browse filter ID to the URL params
     * @param e Mouse click event
     * @param type Browse type
     * @param value Browse filter value
     */
    private onFilterClick(e: React.MouseEvent<HTMLAnchorElement>, type: BrowseType, value: any) {
        const filters = { ...this.state.filters };
        (filters as any)[type.name].toggled = false;
        this.setState({ ...this.state, filters });
        this.props.updateQuery({ browse: "", [type.param]: value, page: "" });
        e.preventDefault();
        return false;
    }

    private onMoreClick() {
        const urlQuery = this.getUrlQuery();
        const page = parseInt(urlQuery.page || "1", 10);
        this.props.updateQuery({ page: (page + 1).toString() });
    }

    /**
     * Resets all filter checks based on current url query
     */
    private onQueryUpdated(query?: string) {
        const appliedFilters: M.Search.AppliedFilter[] = [];
        const urlQuery = QueryString.parse((query || "")) as UrlQuery;
        const search = decodeURIComponent(urlQuery.q || "");
        const catIds = (urlQuery.cat || ",").split(",").filter((x) => x !== "").map((x) => parseInt(x, 10));
        const locations = decodeURIComponent(urlQuery.loc || "|").split("|").filter((x) => x !== "")
            .map((x) => {
                // location string can be of format [state]_[country] or [country]
                const parts = x.split("_").filter((y) => y !== "");
                return {
                    stateCode: parts.length === 2 ? parts[0] : undefined,
                    countryCode: parts.length === 2 ? parts[1] : parts[0],
                };
            });

        if (search || search.length >= 2)
            appliedFilters.push({
                id: search,
                name: search,
                type: "q",
                typeName: "Keyword",
            });

        if (catIds.length) {
            for (const cid of catIds) {
                const cat = _.find(CareerCenterJobCategories.Values, (c) => c.id === cid);
                if (cat)
                    appliedFilters.push({
                        id: cat.id.toString(),
                        name: cat.name,
                        type: "cat",
                        typeName: "Job category",
                    });
            }
        }

        if (locations.length) {
            for (const loc of locations) {
                const country = this.props.countries.find((c) => c !== undefined && c.isoCode === loc.countryCode);
                if (country) {
                    const state = _.find(country.states, (s) =>
                        s.code.toLowerCase() === (loc.stateCode || "").toLowerCase()
                        || s.name.toLowerCase() === (loc.stateCode || "").toLowerCase());
                    appliedFilters.push({
                        id: loc.stateCode ? `${loc.stateCode}_${loc.countryCode}` : loc.countryCode,
                        name: loc.countryCode === "US" && state ? state.name : country.name,
                        type: "loc",
                        typeName: "Location",
                    });
                }
            }
        }

        this.setState({
            ...this.state,
            appliedFilters,
            doFilter: true,
        }, () => {
            this.setState({...this.state, doFilter: true});
        });
    }

    private onSearchChange(query: string) {
        if (this._searchTimer !== undefined)
            clearTimeout(this._searchTimer);

        this._searchTimer = setTimeout(() => {
            this.props.updateQuery({ browse: "", page: "", q: (query || "").length < 2 ? "" : query });
        }, 375);
    }

    private triggerDataFilter() {
        this.setState({ ...this.state, doFilter: true });
    }
}

export const JobDirectory: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(JobDirectoryComponent) as any);

interface PreviewProps {
    currentUrl: string;
    job: M.CareerCenter.JobPost;
}

const Preview: React.SFC<PreviewProps> = (props) => {
    const job = props.job;
    const desc = job.descriptionNoHtml || "";

    return <Comp.Generic.WrapperLess>
        <h3 className="jobs-title">
            <Link to={{ pathname: CareerCenterHelper.jobDetailsLink(job), state: { prevPath: props.currentUrl } }}>{job.title}</Link>
        </h3>

        <h3 className="jobs-location">
            {job.organization} &ndash; {M.Generic.AddressHelper.displayCityStateInline(job.location, "name", false)}
        </h3>

        <ul className="jobs-summary">
            <li>Posted {moment(Utility.fromApiDate(job.postedOn)).format("MMM D, YYYY")}</li>
        </ul>

        <div className="jobs-preview" dangerouslySetInnerHTML={{ __html: desc.length > 200 ? (desc.substr(0, 200).trim() + "&hellip;") : desc }} />
    </Comp.Generic.WrapperLess>;
};



// WEBPACK FOOTER //
// ./src/containers/careerCenter/jobDirectory/JobDirectory.tsx